import React, { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import AsyncIcon from '@ux/icon/async-icon.js';

// Removed icon CSS imports; AsyncIcon handles icon rendering without per-icon CSS

import NavBarItem from './NavBarItem';
import { makeUniqueItemKey } from './utils';

import { useGetWindowHref } from '../../lib/useGetWindowHref';
import { isNavItemActive } from '../../lib/linkUtils';

import './NavBar.css';
import { useIntersection } from 'react-use';

const NavBar = (props) => {
  const {
    items
  } = props;

  const browserUrlStr = useGetWindowHref();

  const navRef = useRef(null);
  const firstItemRef = useRef(null);
  const lastItemRef = useRef(null);

  const intersectionEntryFirstItem = useIntersection(firstItemRef, {
    root: navRef.current,
    rootMargin: '0px',
    threshold: 0.97
  });

  const intersectionEntryLastItem = useIntersection(lastItemRef, {
    root: navRef.current,
    rootMargin: '0px',
    threshold: 0.97
  });

  const scrollLeft = useCallback(() => {
    if (navRef.current) {
      navRef.current.scrollBy({
        left: -navRef.current.getBoundingClientRect().width / 3,
        behavior: 'smooth'
      });
    }
  }, [navRef]);

  const scrollRight = useCallback(() => {
    if (navRef.current) {
      navRef.current.scrollBy({
        left: navRef.current.getBoundingClientRect().width / 3,
        behavior: 'smooth'
      });
    }
  }, [navRef]);

  // TODO: confirm items are immutable, they should be
  const itemsJsx = useMemo(() => items.map((item, index) => {
    function getItemRef() {
      if (!index) {
        return firstItemRef;
      }

      if (items.length === index + 1) {
        return lastItemRef;
      }

      return undefined;
    }

    return (
      <NavBarItem
        ref={ getItemRef() }
        { ...item }
        active={ item.active ?? isNavItemActive(item, browserUrlStr) }
        key={ makeUniqueItemKey(item, index, 'in-page-nav-item') }
      />
    );
  }), [browserUrlStr, items]);

  const showLeftArrow = intersectionEntryFirstItem && !intersectionEntryFirstItem?.isIntersecting;
  const showRightArrow = intersectionEntryLastItem && !intersectionEntryLastItem?.isIntersecting;
  const isScrollable = showLeftArrow || showRightArrow;

  return (
    <div key='in-page-nav' className='in-page-nav'>
      <div className={ classNames(
        'in-page-nav-controls',
        { 'hide-arrows': !isScrollable }
      ) }>
        <nav
          ref={ navRef }
          className={ classNames(
            'in-page-nav-wrap',
            { 'fade-left': showLeftArrow, 'fade-right': showRightArrow }
          ) }>
          <ul className='in-page-nav-items'>
            { itemsJsx }
          </ul>
        </nav>
        <button
          className={ classNames(
            'in-page-nav-arrow arrow-left',
            { 'arrow-show': showLeftArrow }
          ) }
          onClick={ scrollLeft }>
          <AsyncIcon icon='chevron-left' />
        </button>
        <button
          className={ classNames(
            'in-page-nav-arrow arrow-right',
            { 'arrow-show': showRightArrow }
          ) }
          onClick={ scrollRight }>
          <AsyncIcon icon='chevron-right' />
        </button>
      </div>
    </div>
  );
};

NavBar.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape(NavBarItem.propTypes)).isRequired
};

export default NavBar;
